import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NO_SELECTION_MODIFIERS, SelectionModifiers, UiCheckboxChange } from '../../shared/models/selection.models';

// ─────────────────────────────────────────────────────────────────────────────
// CheckboxComponent (ui-checkbox)
// ─────────────────────────────────────────────────────────────────────────────
// Design-system checkbox: 14×14px box, 3px radius, purple fill when checked,
// white bar when indeterminate. Built on a native <input type="checkbox"> rather
// than mat-checkbox so the geometry matches the spec exactly and there is no
// ripple to fight.
//
// Its reason for existing beyond styling: it reports the keyboard modifiers held
// during the activation, which `MatCheckboxChange` does not carry. Modifiers are
// captured on mousedown/keydown (both fire before `change`) and consumed by the
// next change event, so pointer and keyboard activations are both covered and a
// keyboard activation never inherits stale pointer modifiers.
//
// Click propagation is stopped so it can sit inside clickable table rows.
//
// Usage:
//   <ui-checkbox
//     [checked]="isRowSelected(row)"
//     (changed)="toggleOne(row, $event.checked, $event)">
//   </ui-checkbox>
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'ui-checkbox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
})
export class CheckboxComponent {
  @Input() checked = false;

  /** Tri-state "some but not all" visual, for select-all headers. */
  @Input() indeterminate = false;

  @Input() disabled = false;

  /** Accessible name, used when the checkbox has no visible label. */
  @Input() ariaLabel = '';

  /** Emits the new checked state plus the modifiers held during activation. */
  @Output() changed = new EventEmitter<UiCheckboxChange>();

  /** Modifiers captured on the gesture that is about to produce a `change`. */
  private pendingModifiers: SelectionModifiers = NO_SELECTION_MODIFIERS;

  // ── Event handlers ─────────────────────────────────────────────────────────

  /** Snapshot modifiers before the browser emits `change`. */
  captureModifiers(event: MouseEvent | KeyboardEvent): void {
    this.pendingModifiers = { shiftKey: event.shiftKey, ctrlKey: event.ctrlKey || event.metaKey };
  }

  /** Keep row-level click handlers (navigation, expand…) from firing. */
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  onChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const modifiers = this.pendingModifiers;

    // Consume the snapshot so a later keyboard activation starts clean.
    this.pendingModifiers = NO_SELECTION_MODIFIERS;

    this.changed.emit({ checked, ...modifiers });
  }
}
